
import { TSX, Component, Prop, Vue } from 'vue-facing-decorator';
import { Action, State } from 'vuex-facing-decorator';
import { LockDec } from '@/decorators';
interface Props {
  options: any
};

function emptyPerson() {
  return {
    name: "",
    mobile: "",
    province: "",
    city: "",
    county: "",
    address: "",
  }
}

/** 省市区在表单里是三个字段，级联组件收发的是一整个数组，来回各转一次 */
function toArea(form) {
  return [form.province, form.city, form.county];
}
function fromArea(form, v) {
  let list = v || [];
  form.province = list[0] || "";
  form.city = list[1] || "";
  form.county = list[2] || "";
}

/**
 * 收发件人都要这几项，校验共用一份。
 * 只卡「一定能填」的：省市区里市和区在用户选过地区之后级联一定会补上，
 * 但万一碰上两级数据（部分直辖市）就没有区，硬卡反而发不出去，缺了让后端报错更准
 */
function checkPerson(form, label) {
  let missing = [
    [form.name, `${label}姓名`],
    [form.mobile, `${label}手机号`],
    [form.province, `${label}地区`],
    [form.address, `${label}详细地址`],
  ].find((item) => !item[0]);
  return missing ? `请填写${missing[1]}` : "";
}

@Component({
})
export default class extends TSX<Props>()(Vue) implements Props {

  @Prop()
  options: any;

  @State("express", {
    namespace: "store"
  })
  expressList

  @Action("store/getExpress")
  getExpress

  loading = false
  submitLoading = false
  orderInfo: any = {
    orderGoods: []
  }
  /** 可选发货仓库，只是寄件人信息的预填来源，改了不影响仓库本身 */
  warehouseList: any[] = []
  warehouseId: number = null
  /** 收货人信息，进弹窗时用订单上的值初始化，之后可以改 */
  receiverForm = emptyPerson()
  /** 发货人信息，进弹窗时用所选仓库的值初始化，之后可以改 */
  senderForm = emptyPerson()
  sendList = [
    { name: "快递发货", value: 1 },
    { name: "无需物流", value: 2 },
  ]

  ruleForm = {
    /** 只放勾选中的行，每项是 { id: orderGoods.id, num: 本次发几件 } */
    goodsList: [] as { id: number, num: number }[],
    type: 1,
    /** 默认不叫快递，运单号手工录入 */
    isCallExpress: false,
    expressBill: "",
    expressId: null,
  }

  /**
   * 还能发几件 = 购买数量 - 已发货数量。
   * 不看 packId：一条商品可以分多个包裹发，packId 只记得住最后一个，
   * 判断发完没有以后端的 sendNumber 为准
   */
  remainNum(row) {
    return Math.max((row.num || 0) - (row.sendNumber || 0), 0);
  }

  statusText(row) {
    let sent = row.sendNumber || 0;
    if (sent <= 0) return "待发货";
    if (sent >= (row.num || 0)) return "已发货";
    return "部分发货";
  }

  getSendItem(id) {
    return this.ruleForm.goodsList.find((item) => item.id == id) || null;
  }

  setSendNum(id, num) {
    let item = this.getSendItem(id);
    if (item) item.num = num;
  }

  /** 顶部统计按「件」算，不再按行数，和部分发货对得上 */
  get goodsInfo() {
    let waitSend = 0;
    let send = 0;
    (this.orderInfo.orderGoods || []).forEach((row) => {
      send += row.sendNumber || 0;
      waitSend += this.remainNum(row);
    });
    return { waitSend, send };
  }

  /** 还有货没发完的行，全选只勾这些 */
  get waitSendGoods() {
    return (this.orderInfo.orderGoods || []).filter((row) => this.remainNum(row) > 0);
  }

  get checkAll() {
    if (!this.waitSendGoods.length) return false;
    return this.waitSendGoods.every((row) => !!this.getSendItem(row.id));
  }

  get checkIndeter() {
    if (this.checkAll) return false;
    return this.ruleForm.goodsList.length > 0;
  }

  async created() {
    this.getExpress();
    this.loading = true;
    await this.getData();
    await this.getWarehouse();
    this.loading = false;
  }

  async getData() {
    let { error, data } = await this.$post("/manager/order/view", {
      id: this.options.id,
    });
    if (error) return;
    this.orderInfo = data;
    /** 收货人拿订单上的值打底 */
    this.receiverForm = {
      name: data.username || "",
      mobile: data.mobile || "",
      province: data.province || "",
      city: data.city || "",
      county: data.county || "",
      address: data.address || "",
    };
  }

  get receiverArea() {
    return toArea(this.receiverForm);
  }
  set receiverArea(v) {
    fromArea(this.receiverForm, v);
  }
  get senderArea() {
    return toArea(this.senderForm);
  }
  set senderArea(v) {
    fromArea(this.senderForm, v);
  }

  get receiverError() {
    return checkPerson(this.receiverForm, "收货人");
  }
  get senderError() {
    return checkPerson(this.senderForm, "发货人");
  }

  /**
   * 仓库页开新窗口，当前这个发货弹窗不动 —— 配完仓库切回来接着发。
   * $goto 带第二个参数就是 window.open，必须在这里同步调用，
   * 放到 await 之后会被浏览器当弹窗拦掉
   */
  gotoWarehouse() {
    let id = this.warehouse && this.warehouse.id;
    this.$goto({
      path: "/shop/order/warehouse/operat",
      query: id ? { id } : undefined,
    }, '_blank');
  }

  mounted() {
    /** 仓库是在新窗口里改的，切回来的时候得能看到刚改的内容 */
    window.addEventListener("focus", this.onWindowFocus);
  }
  unmounted() {
    window.removeEventListener("focus", this.onWindowFocus);
  }

  get warehouse() {
    return this.warehouseList.find((item) => item.id == this.warehouseId) || null;
  }

  /** 上一次由仓库带出来的发货人信息，用来判断用户有没有手改过 */
  appliedSender = "";

  async fetchWarehouseList() {
    /** pageSize: -1 是这个项目里「不分页、全都要」的写法 */
    let { error, data } = await this.$post("/manager/warehouse/list", { pageSize: -1 });
    if (error) return;
    this.warehouseList = data.list || [];
  }

  async getWarehouse() {
    await this.fetchWarehouseList();
    /** 默认选中默认仓库，没有默认的就用第一个 */
    let target = this.warehouseList.find((item) => item.isDefault) || this.warehouseList[0];
    if (!target) return;
    this.warehouseId = target.id;
    this.applyWarehouse(target.id);
  }

  /**
   * 从新窗口配置完仓库切回来：刷新列表。
   * 发货人信息只在用户没手改过的情况下重新带出，
   * 不然会把他在弹窗里敲的内容冲掉
   */
  async onWindowFocus() {
    await this.fetchWarehouseList();
    if (JSON.stringify(this.senderForm) !== this.appliedSender) return;
    let target = this.warehouse || this.warehouseList.find((item) => item.isDefault) || this.warehouseList[0];
    if (!target) return;
    this.warehouseId = target.id;
    this.applyWarehouse(target.id);
  }

  /** 换仓库就把发货人信息整个换掉，之前手改的内容会丢 —— 这正是切换的本意 */
  applyWarehouse(id) {
    let item = this.warehouseList.find((row) => row.id == id);
    if (!item) return;
    this.senderForm = {
      name: item.senderName || "",
      mobile: item.senderMobile || "",
      province: item.province || "",
      city: item.city || "",
      county: item.county || "",
      address: item.address || "",
    };
    this.appliedSender = JSON.stringify(this.senderForm);
  }

  checkAllGoods(bool) {
    if (!bool) {
      this.ruleForm.goodsList = [];
      return;
    }
    this.waitSendGoods.forEach((row) => {
      if (!this.getSendItem(row.id)) {
        /** 勾选时默认把还能发的全发掉，要少发再改「本次发货」 */
        this.ruleForm.goodsList.push({ id: row.id, num: this.remainNum(row) });
      }
    });
  }

  changeGoods(row) {
    let index = this.ruleForm.goodsList.findIndex((item) => item.id == row.id);
    if (index >= 0) {
      this.ruleForm.goodsList.splice(index, 1);
      return;
    }
    let remain = this.remainNum(row);
    if (remain <= 0) return;
    this.ruleForm.goodsList.push({ id: row.id, num: remain });
  }

  @LockDec("submitLoading")
  async submitForm() {
    if (!this.ruleForm.goodsList.length) {
      return this.$error("请选择发货商品");
    }
    let payload: any = {
      id: this.options.id,
      /** 后端收的是「发哪一行、发几件」，不是 id 数组 */
      goodsList: this.ruleForm.goodsList.map((item) => {
        return { id: item.id, num: Number(item.num) };
      }),
      type: this.ruleForm.type,
    };
    /**
     * 只有快递发货才要寄件人/收件人那一堆字段 —— 无需物流时后端会把它们整段剥掉。
     * 不叫快递才传运单号，叫快递时后端也会把 expressBill 剥掉，传了不认
     */
    if (this.ruleForm.type == 1) {
      if (this.senderError) return this.$error(this.senderError);
      if (this.receiverError) return this.$error(this.receiverError);
      if (!this.ruleForm.expressId) return this.$error("请选择快递公司");
      if (!this.ruleForm.isCallExpress && !this.ruleForm.expressBill) {
        return this.$error("请输入物流单号");
      }
      payload.isCallExpress = this.ruleForm.isCallExpress;
      payload.expressId = this.ruleForm.expressId;
      if (!this.ruleForm.isCallExpress) {
        payload.expressBill = this.ruleForm.expressBill;
      }
      Object.assign(payload, {
        senderName: this.senderForm.name,
        senderMobile: this.senderForm.mobile,
        senderProvince: this.senderForm.province,
        senderCity: this.senderForm.city,
        senderCounty: this.senderForm.county,
        senderAddress: this.senderForm.address,
        receiverName: this.receiverForm.name,
        receiverMobile: this.receiverForm.mobile,
        receiverProvince: this.receiverForm.province,
        receiverCity: this.receiverForm.city,
        receiverCounty: this.receiverForm.county,
        receiverAddress: this.receiverForm.address,
      });
    }
    await this.$postNotify("/manager/order/send-gooods", payload);
    this.$emit('resolve');
  }
}
